import { createSelector, createSlice, current } from '@reduxjs/toolkit'
import { isEqual } from 'lodash'
import { RootState } from '../../../../root.reducer'
import { NEW_EVAL_FORM, SINGLE_ANSWER_DEFAULTS } from '../efmConstants'
import { CurrentFormState, NewFormState, OptionState, QuestionState, SectionState } from '../efmTypes'
import { arrayMove } from '../evaluationFormManager.helpers'

interface InitialState {
  currentForm: CurrentFormState,
  initialForm: NewFormState,
}
const initialState: InitialState = {
  currentForm: {
    ...NEW_EVAL_FORM,
    creator: '',
    createdTz: '',
    created: 0,
    updater: '',
    updatedTz: '',
    updated: 0,
  },
  initialForm: {
    ...NEW_EVAL_FORM,
  },
}

type FormKeys = keyof Omit<NewFormState, 'sections'>
type SectionKeys = keyof Omit<SectionState, 'questions'>
type QuestionKeys = keyof Omit<QuestionState, 'options'>
type OptionKeys = keyof OptionState

const reorderList = <T extends SectionState | QuestionState | OptionState>(items: T[]): T[]  => {
  return items.map((item, index) => {
    return {
      ...item,
      order: index + 1,
    }
  })
}

// TODO this should not be exported after refactor is finished, one location remaining
export const assignUniqueKey = (array: SectionState[]): number => {
  let value = -1
  for (const section of array) {
    if (Math.abs(section.id) >= Math.abs(value)) {
      value = (Math.abs(section.id) + 1) * -1
    }

    const questions = section.questions
    for (const question of questions) {
      if (Math.abs(question.id) >= Math.abs(value)) {
        value = (Math.abs(question.id) + 1) * -1
      }

      const options = question.options
      if (options) {
        for (const option of options) {
          if (Math.abs(option.id) >= Math.abs(value)) {
            value = (Math.abs(option.id) + 1) * -1
          }
        }
      }
    }
  }
  return value
}

const efmFormSlice = createSlice({
  name: 'efmForm',
  initialState,
  reducers: {
    setCurrentForm(state: InitialState, action) {
      state.currentForm = action.payload
    },
    setInitialForm(state: InitialState, action) {
      state.initialForm = action.payload
    },
    // create_
    addNewSection(state: InitialState, action: { payload: string }) {
      const { sections } = state.currentForm
      const newSection = {
        id: assignUniqueKey(current(sections)),
        order: sections.length + 1,
        weight: 1,
        name: action.payload,
        displayOnLoad: true,
        questions: [],
      }
      sections.push(newSection)
    },
    addNewQuestion(state: InitialState, action: { payload: { sectionId: number, questionTitle: string }}) {
      const { sectionId, questionTitle } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      const newQuestion = {
        id: assignUniqueKey(current(sections)),
        text: questionTitle,
        description: '',
        order: section?.questions.length ? section.questions.length + 1 : 1,
        weight: 1,
        displayOnLoad: true,
        options: [],
      }
      if (section) {
        section.questions.push(newQuestion)
      }
    },
    addNewOption(state: InitialState, action: { payload: { sectionId: number, questionId: number, optionLabel: string }}) {
      const { sectionId, questionId, optionLabel } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      const question = section?.questions.find(question => question.id === questionId)
      const newOption = {
        ...SINGLE_ANSWER_DEFAULTS,
        id: assignUniqueKey(current(sections)),
        order: question?.options.length ? question.options.length + 1 : 1,
        label: optionLabel,
        isDefault: question?.options.length === 0 ? true : false,
        displaySection: null,
        displayQuestion: null,
      }
      if (question) {
        question.options.push(newOption)
      }
    },
    // update_
    updateFormField<T extends FormKeys>(state: InitialState, action: { payload: { field: T, value: CurrentFormState[T] }}) {
      const { field, value } = action.payload
      state.currentForm[field] = value
    },
    updateSectionField<T extends SectionKeys>(state: InitialState, action: { payload: { sectionId: number, field: T, value: SectionState[T] }}) {
      const { sectionId, field, value } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      if (section) {
        section[field] = value
      }
    },
    updateQuestionField<T extends QuestionKeys>(state: InitialState, action: { payload: { sectionId: number, questionId: number, field: T, value: QuestionState[T] }}) {
      const { sectionId, questionId, field, value } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      const question = section?.questions.find(question => question.id === questionId)
      if (section && question) {
        question[field] = value
      }
    },
    // sets the correct section or question displayOnLoad boolean to false if it is currently true
    setDisplayOnLoadValue(state: InitialState, action: { payload: { sectionOrdinal: number | null, questionOrdinal: number | null, sectionId: number, optionId: number, idForGlobal: number | null }}) {
      const { sectionOrdinal, questionOrdinal, sectionId } = action.payload
      if (sectionOrdinal) {
        const section = state.currentForm.sections.find(section => section.order === sectionOrdinal)
        if (section?.displayOnLoad) {
          section.displayOnLoad = false
        }
      }
      if (questionOrdinal) {
        const section = state.currentForm.sections.find(section => section.id === sectionId)
        const question = section?.questions.find(question => question.order === questionOrdinal)
        if (question?.displayOnLoad) {
          question.displayOnLoad = false
        }
      }
    },
    updateOptionField<T extends OptionKeys>(state: InitialState, action: { payload: { sectionId: number, questionId: number, optionId: number, field: T, value: OptionState[T] }}) {
      const { sectionId, questionId, optionId, field, value } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      const question = section?.questions.find(question => question.id === questionId)
      const option = question?.options.find(option => option.id === optionId)
      if (section && question && option) {
        option[field] = value
      }
    },
    // move_
    changeSectionOrder(state: InitialState, action: { payload: { sectionIndex: number, direction: number }}) {
      const { sectionIndex, direction } = action.payload
      const { sections } = state.currentForm
      const updatedSections = arrayMove(sections, sectionIndex, sectionIndex + direction)
      if (updatedSections) {
        state.currentForm.sections = reorderList(updatedSections)
      }
    },
    changeQuestionOrder(state: InitialState, action: { payload: { sectionId: number, questionIndex: number, direction: number }}) {
      const { sectionId, questionIndex, direction } = action.payload
      const { sections } = state.currentForm
      const section = sections.find(section => section.id === sectionId)
      const updatedQuestions = arrayMove(section?.questions, questionIndex, questionIndex + direction)
      if (section && updatedQuestions) {
        section.questions = reorderList(updatedQuestions)
      }
    },
    changeQuestionSection(state: InitialState, action: { payload: { targetSection: number, currentQuestion: QuestionState }}) {
      const { targetSection, currentQuestion } = action.payload
      const { sections } = state.currentForm
      const uid = assignUniqueKey(current(sections))
      const updatedSections = sections.map(section => {
        const updatedQuestions = section.questions.filter(question => question.id !== currentQuestion.id)
        return { ...section, questions: reorderList(updatedQuestions) }
      })
      const targetIndex = updatedSections.findIndex(section => section.id === targetSection)
      const newOrder = updatedSections[targetIndex]?.questions?.length
      // add question to the end of section
      updatedSections[targetIndex]?.questions.push({ ...currentQuestion, id: uid, order: newOrder ? newOrder + 1 : 1 })
      state.currentForm.sections = updatedSections
    },
    changeAnswerGroupTemplate(state: InitialState, action: { payload: { sectionId: number, questionId: number, newOptions: OptionState[]}}) {
      const { sectionId, questionId, newOptions } = action.payload
      const { sections } = state.currentForm

      const section = sections.find(section => section.id === sectionId)
      const question = section?.questions.find(question => question.id === questionId)
      if (question) {
        question.options = reorderList(newOptions)
      }
    },
    // delete_
    deleteSection(state: InitialState, action: { payload: { sectionId: number }}) {
      const { sectionId } = action.payload
      const filteredSections = state.currentForm.sections.filter(section => section.id !== sectionId)
      state.currentForm.sections = filteredSections.length > 0 ? reorderList(filteredSections) : []
    },
    deleteQuestion(state: InitialState, action: { payload: { sectionId: number, questionId: number }}) {
      const { sectionId, questionId } = action.payload
      const section = state.currentForm.sections.find(section => section.id === sectionId)
      const filteredQuestions = section?.questions.filter(question => question.id !== questionId)
      if (section && filteredQuestions) {
        section.questions = filteredQuestions.length > 0 ? reorderList(filteredQuestions) : []
      }
    },
    deleteOption(state: InitialState, action: { payload: { sectionId: number, questionId: number, optionId: number }}) {
      const { sectionId, questionId, optionId } = action.payload
      const section = state.currentForm.sections.find(section => section.id === sectionId)
      const question = section?.questions.find(question => question.id === questionId)
      // We have to reassign default if it happened to get deleted
      const shouldAssignNewDefault = question?.options.find(option => option.id === optionId)?.isDefault ?? false
      const filteredOptions = question?.options.filter(option => option.id !== optionId)
      if (section && question && filteredOptions) {
        if (filteredOptions.length === 0) {
          question.options = []
        }
        if (filteredOptions.length > 0) {
          if (!shouldAssignNewDefault) {
            question.options = reorderList(filteredOptions)
          } else {
            const maxValue = filteredOptions.reduce((a, b) => a.points > b.points ? a : b).points
            const maxValueIndex = filteredOptions.findIndex(option => option.points === maxValue)
            const updatedOptions = filteredOptions.map((option, index) => {
              if (index === maxValueIndex) {
                return { ...option, isDefault: true }
              }
              return option
            })
            question.options = reorderList(updatedOptions)
          }
        }
      }
    },
  },
})

export const isFormDirty = createSelector(
  (state: RootState) => state.efmForm.currentForm,
  (state: RootState) => state.efmForm.initialForm,
  (currentForm, initialForm) => {
    return !isEqual(currentForm, initialForm)
  },
)

export const findBandRangeLimit = createSelector(
  (state: RootState) => state.efmForm.currentForm.sections,
  sections => {
    const values: { pointMax: number, pointMin: number, kpiMax: null | number, kpiMin: null | number} = {
      pointMax: 0,
      kpiMax: null,
      pointMin: 0,
      kpiMin: null,
    }
    for (const section of sections) {
      const questions = section.questions
      for (const question of questions) {
        // Points
        const pointOptions = question.options.filter(entry => entry.type !== 'KPI')
        // Min and Max value
        const minPointOption = pointOptions.length > 0 ? Math.min(...pointOptions.map(option => option.points)) : 0
        const maxPointOption = pointOptions.length > 0 ? Math.max(...pointOptions.map(option => option.points)) : 0
        // Sum
        values.pointMin += minPointOption
        values.pointMax += maxPointOption
        // KPIs
        const kpiOptions = question.options.filter(entry => entry.type === 'KPI')
        // Min and Max value
        const minKpiOption = Math.min(...kpiOptions.map(option => option.points))
        const maxKpiOption = Math.max(...kpiOptions.map(option => option.points))
        // Unlike point min/max, KPIs must default to null
        // If there is a KPI option in the form -> set the KPI min/max
        if (kpiOptions.length > 0) {
          if (!values.kpiMin) {
            values.kpiMin = minKpiOption
          } else {
            values.kpiMin = Math.min(values.kpiMin, minKpiOption)
          }
          if (!values.kpiMax) {
            values.kpiMax = maxKpiOption
          } else {
            values.kpiMax = Math.max(values.kpiMax, maxKpiOption)
          }
        }
      }
    }
    const bandMin = Math.min(values.kpiMin ?? values.pointMin, values.pointMin)
    const bandMax = Math.max(values.kpiMax ?? values.pointMax, values.pointMax)
    return [bandMin, bandMax] as const
  },
)

export const sectionWeightTotal = createSelector(
  (state: RootState) => state.efmForm.currentForm.sections,
  sections => {
    return sections.reduce((acc, section: { weight: number }) => acc + section.weight, 0)
  },
)

export const kpiOptionList = createSelector(
  (state: RootState) => state.efmForm.currentForm.sections,
  sections => {
    const result = []
    for (const section of sections) {
      const questions = section.questions

      if (questions) {
        for (const question of questions) {
          const options = question.options

          if (options) {
            for (const option of options) {
              if (option.type === 'KPI') {
                result.push({
                  id: option.id,
                  priority: option.kpiPriority,
                  questionName: question.text,
                  answerName: option.label,
                  formScore: option.points,
                  sectionName: section.name,
                  sectionId: section.id,
                  questionId: question.id,
                })
              }
            }
          }
        }
      }
    }
    const sortedList = result.sort((a, b) => {
      // values of zero will represent newly added kpi options
      if (a.priority === 0) {
        return 1
      }
      // we will push all zeroes to the end of this sorted array
      if (b.priority === 0) {
        return -1
      }
      // kpi priority values of all options which do not match their index + 1 will get adjusted by a listener action
      return a.priority - b.priority
    })
    return sortedList
  },
)

export const efmForm = (state: RootState): InitialState => state.efmForm
export const {
  addNewOption,
  addNewQuestion,
  addNewSection,
  changeAnswerGroupTemplate,
  changeQuestionOrder,
  changeQuestionSection,
  changeSectionOrder,
  deleteOption,
  deleteQuestion,
  deleteSection,
  setCurrentForm,
  setDisplayOnLoadValue,
  setInitialForm,
  updateFormField,
  updateOptionField,
  updateQuestionField,
  updateSectionField,
} = efmFormSlice.actions
export default efmFormSlice.reducer
